import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../ContentContext";
import { VideoApi } from "../api";
import {
  HeroCarousel,
  SectionHead,
  PackSlider,
  Split,
  FeatureBlock,
  FestivalCard,
} from "../components/Common";

// Promo YouTube videos — a horizontally scrollable thumbnail row, newest first.
// The centred heading + description are managed from admin (Videos page).
function VideoRow() {
  const [videos, setVideos] = useState([]);
  const [heading, setHeading] = useState({
    title: "Watch & Explore",
    description: "",
  });
  useEffect(() => {
    let alive = true;
    VideoApi.list()
      .then((r) => {
        if (!alive) return;
        setVideos(r.items || []);
        if (r.heading)
          setHeading({
            title: r.heading.title || "Watch & Explore",
            description: r.heading.description || "",
          });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  if (!videos.length) return null;
  return (
    <section className="alt-bg">
      <div className="container">
        <SectionHead
          tag="Become Our Next"
          title={heading.title}
          text={heading.description}
        />
        <div className="video-row">
          {videos.map((v) => (
            <a
              key={v._id}
              className="video-card"
              href={v.url}
              target="_blank"
              rel="noreferrer"
            >
              <span className="video-thumb">
                {v.thumbnail ? (
                  <img
                    src={v.thumbnail}
                    alt={v.title || "Video"}
                    loading="lazy"
                  />
                ) : null}
                <span className="video-play">
                  <i className="fas fa-play" />
                </span>
              </span>
              {v.title ? <span className="video-title">{v.title}</span> : null}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

// Gold eyebrow tags, matching the live site.
const FEATURE_TAGS = {
  "Strict Quality Assurance": "Quality Control",
  "Safety You Trust": "Quality Control",
  "Serving Global Markets": "Global Presence",
};

export default function Home() {
  const {
    brands = [],
    festivals = [],
    features = [],
    storyCards = [],
    aboutHome = {},
    certification = {},
  } = useContent();

  return (
    <>
      <HeroCarousel />

      {/* Our Brands — the brands (name + image) managed in Website Content */}
      {brands.length ? (
        <section id="brands">
          <div className="container">
            <SectionHead title="Our Brands" />
            <div className="brand-grid">
              {brands.map((b) => (
                <Link
                  className="brand-box"
                  key={b.slug}
                  to={`/${b.slug}`}
                >
                  {b.image ? <img src={b.image} alt={b.name} /> : null}
                  <h3>{b.name}</h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Catalogue showcase: one section per brand, as on the source site */}
      {brands.map((b, i) => {
        const codes = b.products?.basmati || [];
        if (!codes.length) return null;
        return (
          <section
            key={b.slug}
            id={`${b.slug}-products`}
            className={i % 2 === 0 ? "alt-bg" : ""}
          >
            <div className="container">
              <SectionHead
                tag="Premium Collection"
                title={`${b.name} Premium Products`}
                text={`Explore our premium range of ${b.name} Basmati Rice products.`}
              />
              <PackSlider brand={b} category="basmati" codes={codes} />
              <div style={{ textAlign: "center", marginTop: 30 }}>
                <Link className="btn-gold" to={`/${b.slug}`}>
                  View All {b.name} Products
                </Link>
              </div>
            </div>
          </section>
        );
      })}

      {/* Promo videos — scrollable thumbnails, just above certifications */}
      <VideoRow />

      {/* Certifications — large full-width image */}
      {certification.image ? (
        <section className="grey-bg">
          <div className="container">
            <SectionHead
              tag="Quality Certification"
              title={certification.title}
              text={certification.text}
            />
            <a
              className="iso-image"
              href={certification.image}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={certification.image}
                alt="Certification"
                loading="lazy"
              />
            </a>
          </div>
        </section>
      ) : null}

      {/* Festivals — 2-up horizontal cards */}
      {festivals.length ? (
        <section className="alt-bg">
          <div className="container">
            <SectionHead
              tag="Food & Festivals"
              title="Celebrating Every Festival With Golden Taj Rice"
              text="From festive feasts to family gatherings, Golden Taj Rice brings authentic taste and aroma to every special occasion."
            />
            <div className="festival-grid">
              {festivals.map((f) => (
                <FestivalCard key={f.title} {...f} />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* Feature bands: centred heading + full-width image */}
      {features.map((f) => (
        <FeatureBlock
          key={f.title}
          tag={f.tag || FEATURE_TAGS[f.title]}
          {...f}
        />
      ))}

      {/* About — the one genuine two-column split */}
      {aboutHome.image ? (
        <section className="alt-bg">
          <div className="container">
            <Split
              tag="About Us"
              title={aboutHome.title}
              text={aboutHome.text}
              image={aboutHome.image}
              cta={{ label: "Read More", to: "/company-profile" }}
            />
          </div>
        </section>
      ) : null}

      {/* Story blocks — same feature-band structure */}
      {storyCards.map((c) => (
        <FeatureBlock key={c.title} tag={c.tag} {...c} />
      ))}
    </>
  );
}
